/**
 * Verification routes:
 *   - `/agents` — section home rendered by VerificationShell.
 *   - `/agents/_proof/*` — relocated RO-0-05 transport prove-outs
 *
 * Prove-outs live under `/agents/_proof` so they never occupy the product home.
 */

import * as React from "react";
import { z } from "zod";
import { createRoute, redirect } from "@tanstack/react-router";
import { mutate } from "swr";
import { Button, formatDateTime } from "@excellent/ui";
import { useApi, apiCall, apiKey } from "@excellent/ui/api";
import { contextPack } from "@excellent/ui/api/contracts/context-pack";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";

export const agentsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents",
  component: () => null,
});

// ── Relocated RO-0-05 prove-outs ─────────────────────────────────────────────

function Proof({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <section className="px-6 py-8 flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="type-heading font-medium text-foreground">{title}</h1>
        <p className="type-subtext text-muted-foreground">{note}</p>
      </header>
      {children}
    </section>
  );
}

function AgentsProofFts() {
  const { data, error, isLoading } = useApi(contextPack.ftsStatus, {});
  return (
    <Proof
      title="Context pack FTS"
      note="RO-0-05 prove-out · GET /agents/api/context-pack/fts through the Hono host"
    >
      {isLoading && !data && (
        <p className="type-body text-muted-foreground">Checking context proof...</p>
      )}
      {error && (
        <p className="type-body text-destructive">
          {String(error.message ?? error)}
        </p>
      )}
      {data && (
        <div className="flex flex-col gap-3">
          <p data-testid="fts-indexed" className="type-body text-foreground">
            <span className="font-mono type-heading">{data.indexed}</span>{" "}
            <span className="text-muted-foreground">
              documents indexed in the vault
            </span>
          </p>
          <dl data-testid="fts-by-kind" className="flex flex-col gap-1 type-body">
            {Object.entries(data.byKind).map(([kind, n]) => (
              <div key={kind} className="flex gap-3">
                <dt className="w-40 shrink-0 text-muted-foreground">{kind}</dt>
                <dd className="font-mono text-foreground">{n}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </Proof>
  );
}

function AgentsProofRetrieval() {
  const { data } = useApi(contextPack.ftsStatus, {});
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState<string | null>(null);

  async function save() {
    if (!data) return;
    setSaving(true);
    try {
      await apiCall(contextPack.saveConfig, {
        body: { tokenBudget: data.tokenBudget },
      });
      await mutate(apiKey(contextPack.ftsStatus, {}));
      setSaved(new Date().toISOString());
    } finally {
      setSaving(false);
    }
  }

  return (
    <Proof
      title="Retrieval"
      note="RO-0-05 prove-out · POST /agents/api/context-pack/config"
    >
      <Button
        onClick={save}
        disabled={saving || !data}
        data-testid="save-config"
        className="self-start"
      >
        {saving ? "Saving…" : "Persist current token budget"}
      </Button>
      {saved && (
        <p data-testid="save-result" className="type-subtext text-muted-foreground">
          written and revalidated at {formatDateTime(saved)}
        </p>
      )}
    </Proof>
  );
}

const packSearchSchema = routeSearchSchema({
  subject: z.string().optional(),
  budget: z.coerce.number().int().positive().max(32000).optional(),
});

/** Legacy URLs redirect into the `_proof` namespace. */
export const agentsRetrievalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/retrieval",
  beforeLoad: () => {
    throw redirect({ to: "/agents/_proof/retrieval", replace: true });
  },
});

export const agentsPackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/pack",
  beforeLoad: () => {
    throw redirect({ to: "/agents/_proof/pack", replace: true });
  },
});

export const agentsProofFtsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/_proof/fts",
  component: AgentsProofFts,
});

export const agentsProofRetrievalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/_proof/retrieval",
  component: AgentsProofRetrieval,
});

function AgentsProofPack() {
  const search = agentsProofPackRoute.useSearch();
  const question = search.subject ?? search.q ?? "what is in flight right now";
  const [pack, setPack] = React.useState<{
    tokenEstimate: number;
    count: number;
  } | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  async function build() {
    setError(null);
    try {
      const result = await apiCall(contextPack.build, {
        body: { question, tokenBudget: search.budget },
      });
      setPack({
        tokenEstimate: result.tokenEstimate,
        count: result.items.length,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Proof
      title="Context pack"
      note="RO-0-05 prove-out · POST /agents/api/context-pack"
    >
      <p className="type-body text-muted-foreground">
        subject <span className="font-mono text-foreground">{question}</span>
      </p>
      <Button onClick={build} data-testid="build-pack" className="self-start">
        Assemble pack
      </Button>
      {error && <p className="type-body text-destructive">{error}</p>}
      {pack && (
        <p data-testid="pack-result" className="type-body text-foreground">
          <span className="font-mono">{pack.count}</span> items ·{" "}
          <span className="font-mono">{pack.tokenEstimate}</span> tokens
        </p>
      )}
    </Proof>
  );
}

export const agentsProofPackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/_proof/pack",
  validateSearch: packSearchSchema,
  component: AgentsProofPack,
});
